/* eslint-disable max-lines */
import React from 'react';
import { describe, PropTypes } from '@elliemae/ds-props-helpers';
import { noop } from 'lodash-es';
import { Grid } from '@elliemae/ds-grid';
import { WarningTriangle } from '@elliemae/ds-icons';
import { DSButton } from '@elliemae/ds-legacy-button-v1';
import { useTheme } from '@elliemae/ds-system';
import { ModalPosition, ModalBox, BackShadow, ModalIcon, ModalTitle, ModalDescription, BodyFix } from './styled.js';

const Modal = ({
  zIndex = 1,
  title = '',
  description = '',
  onClose = noop,
  showSecondaryAction = true,
  secondaryActionProps = {
    onClick: noop,
    labelText: 'Cancel',
  },
  primaryActionProps = {
    onClick: noop,
    labelText: 'Accept',
  },
  isOpen = false,
  children = undefined,
}) => {
  const theme = useTheme();
  if (!isOpen) return null;
  return (
    <>
      <BodyFix isOpen={isOpen} />
      <BackShadow onClick={onClose} zIndex={zIndex} />
      <ModalPosition zIndex={zIndex} cols={['auto']}>
        {children !== undefined ? (
          <>{children}</>
        ) : (
          <ModalBox
            maxHeight={`${window.innerHeight * 0.75}px`}
            rows={[1, '80px']}
            style={{
              overflow: 'hidden',
            }}
          >
            <Grid
              pl="xs"
              pr="xs"
              style={{
                overflow: 'hidden',
              }}
            >
              <Grid
                rows={['auto', 'auto', 1]}
                style={{
                  overflow: 'hidden',
                }}
              >
                <ModalIcon>
                  <WarningTriangle size="xxl" />
                </ModalIcon>
                <ModalTitle data-testid="modal-title">{title}</ModalTitle>
                <ModalDescription
                  data-testid="modal-desc"
                  style={{
                    overflow: 'auto',
                  }}
                >
                  {description}
                </ModalDescription>
              </Grid>
            </Grid>
            <Grid pt="24px">
              <Grid
                pl="xs"
                pr="xs"
                cols={showSecondaryAction ? [1, 1] : [1]}
                gutter="xs"
                justifyContent="center"
                alignItems="center"
                style={{
                  borderTop: `1px solid ${theme.colors.neutral[100]}`,
                }}
              >
                {showSecondaryAction && (
                  <DSButton
                    {...secondaryActionProps}
                    labelText={secondaryActionProps.labelText || ' Cancel'}
                    buttonType="secondary"
                    data-testid="modal-secondary-btn"
                  />
                )}
                <DSButton
                  labelText={primaryActionProps.labelText || ' Accept'}
                  {...primaryActionProps}
                  buttonType="primary"
                  data-testid="modal-primary-btn"
                />
              </Grid>
            </Grid>
          </ModalBox>
        )}
      </ModalPosition>
    </>
  );
};

const modalProps = {
  isOpen: PropTypes.bool.description('Wheter the modal is open or closed').defaultValue(false),
  zIndex: PropTypes.number.description('z-index value').defaultValue(1),
  title: PropTypes.string.description('Modal s title').isRequired,
  description: PropTypes.string.description('Modal s content').isRequired,
  onClose: PropTypes.func.description('Function executed when the modal closes'),
  showSecondaryAction: PropTypes.bool.description('Wheter to show secondary action button or not').defaultValue(true),
  secondaryActionProps: PropTypes.shape({
    onClick: PropTypes.func,
    labelText: PropTypes.string,
  }).description('Secondary action props'),
  primaryActionProps: PropTypes.shape({
    onClick: PropTypes.func,
    labelText: PropTypes.string,
  }).description('Primary action props'),
};

Modal.displayName = 'Modal';
const ModalWithSchema = describe(Modal);
ModalWithSchema.propTypes = modalProps;

export { Modal, ModalWithSchema };
